import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, FileText, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../api/client";
import { downloadProtectedFile } from "../../../api/files";
import * as documentsApi from "../../../api/documents";
import { DocumentoPrivacy } from "../../../api/documents";
import { Table, TableColumn } from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";

type UploadFormValues = {
  titolo: string;
  descrizione: string;
  versione: string;
  data_pubblicazione: string;
  pubblicato: boolean;
};

export default function Privacy() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const canManage = user?.role === "admin" || user?.role === "staff";

  const [documenti, setDocumenti] = useState<DocumentoPrivacy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function loadDocuments() {
    setIsLoading(true);
    try {
      const { documenti: list } = await documentsApi.listDocuments();
      setDocumenti(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare i documenti."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleTogglePubblicato(doc: DocumentoPrivacy) {
    setTogglingId(doc.id);
    try {
      await documentsApi.updateDocument(doc.id, { pubblicato: !doc.pubblicato });
      showToast(doc.pubblicato ? "Documento nascosto." : "Documento pubblicato.", "success");
      loadDocuments();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare il documento."), "error");
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(doc: DocumentoPrivacy) {
    const confirmed = window.confirm(`Eliminare "${doc.titolo ?? "questo documento"}"? L'operazione è definitiva.`);
    if (!confirmed) return;

    setDeletingId(doc.id);
    try {
      await documentsApi.deleteDocument(doc.id);
      showToast("Documento eliminato.", "success");
      loadDocuments();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il documento."), "error");
    } finally {
      setDeletingId(null);
    }
  }

  const columns: TableColumn<DocumentoPrivacy>[] = [
    {
      key: "titolo",
      header: "Documento",
      render: (d) => (
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-ink/40" aria-hidden="true" />
          {d.titolo ?? "Senza titolo"}
        </span>
      ),
    },
    { key: "versione", header: "Versione", render: (d) => d.versione ?? "—" },
    {
      key: "data_pubblicazione",
      header: "Data pubblicazione",
      render: (d) => (d.data_pubblicazione ? new Date(d.data_pubblicazione).toLocaleDateString("it-IT") : "—"),
    },
    {
      key: "pubblicato",
      header: "Stato",
      render: (d) => <Badge color={d.pubblicato ? "success" : "neutral"}>{d.pubblicato ? "Pubblicato" : "Nascosto"}</Badge>,
    },
    {
      key: "actions",
      header: "Azioni",
      render: (d) => (
        <div className="flex items-center gap-2">
          {d.file_url && (
            <button
              type="button"
              onClick={() =>
                downloadProtectedFile(d.file_url!, d.titolo ? `${d.titolo}.pdf` : "documento.pdf").catch((err) =>
                  showToast(getErrorMessage(err, "Impossibile scaricare il documento."), "error")
                )
              }
              className="text-xs font-semibold text-primary hover:underline"
            >
              Scarica
            </button>
          )}
          {canManage && (
            <>
              <Button
                size="sm"
                variant="ghost"
                loading={togglingId === d.id}
                onClick={() => handleTogglePubblicato(d)}
                aria-label={d.pubblicato ? "Nascondi documento" : "Pubblica documento"}
              >
                {d.pubblicato ? <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> : <Eye className="h-3.5 w-3.5" aria-hidden="true" />}
              </Button>
              <Button size="sm" variant="ghost" className="text-accent" loading={deletingId === d.id} onClick={() => handleDelete(d)} aria-label="Elimina documento">
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Privacy & Documenti</h1>
          <p className="mt-1 text-sm text-ink/50">
            {canManage ? "Tutti i documenti, inclusi quelli non ancora pubblicati." : "Documenti pubblicati."}
          </p>
        </div>

        {canManage && (
          <Button onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Carica documento
          </Button>
        )}
      </header>

      <Table
        columns={columns}
        data={documenti}
        rowKey={(d) => d.id}
        isLoading={isLoading}
        emptyMessage={canManage ? "Nessun documento caricato." : "Nessun documento disponibile al momento."}
      />

      {canManage && (
        <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={loadDocuments} />
      )}
    </div>
  );
}

// ============================================================================
// Modal — upload nuovo PDF
// ============================================================================
function UploadModal({ open, onClose, onUploaded }: { open: boolean; onClose: () => void; onUploaded: () => void }) {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<UploadFormValues>({ defaultValues: { pubblicato: true } });

  function handleClose() {
    reset({ titolo: "", descrizione: "", versione: "", data_pubblicazione: "", pubblicato: true });
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      showToast("Seleziona un file PDF da caricare.", "error");
      return;
    }
    if (file.type !== "application/pdf") {
      showToast("È ammesso solo il formato PDF.", "error");
      return;
    }

    try {
      await documentsApi.uploadDocument({
        file,
        titolo: values.titolo || undefined,
        descrizione: values.descrizione || undefined,
        versione: values.versione || undefined,
        data_pubblicazione: values.data_pubblicazione || undefined,
        pubblicato: values.pubblicato,
      });
      showToast("Documento caricato.", "success");
      handleClose();
      onUploaded();
    } catch (err) {
      showToast(getErrorMessage(err, "Caricamento non riuscito."), "error");
    }
  });

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Carica documento"
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleClose}>
            Annulla
          </Button>
          <Button type="submit" form="upload-form" loading={isSubmitting}>
            Carica
          </Button>
        </>
      }
    >
      <form id="upload-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">
            File PDF <span className="text-accent">*</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink file:mr-3 file:rounded file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary file:font-semibold"
          />
          {fileName && <p className="text-xs text-ink/50">{fileName}</p>}
          <p className="text-xs text-ink/40">Massimo 10MB, solo formato PDF.</p>
        </div>

        <Input label="Titolo" hint="Se vuoto, viene usato il nome del file." {...register("titolo")} />
        <Input label="Versione" placeholder="Es. 2.1" {...register("versione")} />
        <Input label="Data pubblicazione" type="date" {...register("data_pubblicazione")} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Descrizione</label>
          <textarea
            rows={3}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("descrizione")}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("pubblicato")} />
          Pubblica subito (visibile a tutti gli utenti)
        </label>
      </form>
    </Modal>
  );
}
